<template>
  <!-- Wrapper changes depending on if it's a modal or a page -->
  <div :class="isModal ? 'gs-overlay' : 'gs-page-wrapper'">
    
    <!-- <div v-if="!isModal" class="mb-6 mt-2 md:mt-4 pl-1">
      <h2 class="text-2xl font-medium text-gray-900">Getting Started</h2>
    </div> -->

    <div :class="isModal ? 'gs-modal-container' : 'gs-page-container'">
      
      <!-- Close Button: Only shows in Modal mode -->
      <button v-if="isModal" @click="closeModal" class="gs-close-btn">
        <feather-icon icon="XIcon" svgClasses="w-6 h-6" />
      </button>

      <!-- Inner Content Wrapper -->
      <div :class="isModal ? '' : 'gs-page-inner-content'">
        
        <!-- Header -->
        <div class="gs-header">
          <!-- <div v-if="firstOrgName" class="gs-invite-pill">
            <feather-icon icon="StarIcon" svgClasses="w-3 h-3" /> Invited by {{ firstOrgName }}
          </div> -->
          <h1 class="gs-title">Welcome to Getboarded 👋</h1>
          <p class="gs-subtitle">Set up the basics so we can personalize your journey. Two quick steps get you productive.</p>
          
          <!-- Progress Bar -->
          <div class="gs-progress-wrapper">
            <span class="gs-progress-label">Getting started</span>
            <span class="gs-progress-dot">•</span>
            <span class="gs-progress-text">{{ completedCount }} of 2 completed</span>
            <div class="gs-progress-track">
              <div class="gs-progress-fill" :style="{ width: (completedCount * 50) + '%' }"></div>
            </div>
          </div>
        </div>

        <!-- Action Cards -->
        <div class="gs-cards-grid">
          
          <!-- Card 1: Complete Profile -->
          <div class="gs-card" :class="hasProfile ? 'gs-card-completed' : 'gs-card-pending'">
            <div class="gs-card-badge">1</div>
            
            <div class="gs-card-image">
              <img src="@/assets/images/temp-img/profile-complete.svg" alt="Complete Profile" />
            </div>
            
            <h3 class="gs-card-title">Complete your profile</h3>
            <p class="gs-card-desc">Build your talent card and unlock personalized recommendations. Add your CV to get better matching to roles and mentors.</p>
            
            <!-- Action area anchored to the bottom to match Card 2 -->
            <div class="gs-card-action mt-auto flex justify-between items-center w-full">
              
              <!-- Left side: Status Button -->
              <div>
                <button v-if="!hasProfile" @click="editProfile" class="gs-btn-primary">
                  Complete profile <feather-icon icon="ArrowRightIcon" svgClasses="w-4 h-4" />
                </button>
                <div v-else class="gs-btn-completed inline-flex">
                  <feather-icon icon="CheckCircleIcon" svgClasses="w-4 h-4" /> Profile Completed
                </div>
              </div>

              <!-- Right side: NEW SHARE BUTTON -->
              <vx-tooltip text="Share Talent Card" position="bottom" v-if="userData && userData._id">
                <div @click.stop="shareTalentCard" class="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-yellow-200 text-gray-500 hover:text-yellow-700 transition-colors cursor-pointer">
                  <feather-icon icon="Share2Icon" svgClasses="w-6 h-6" />
                </div>
              </vx-tooltip>

            </div>
          </div>

          <!-- Card 2: Create Organization -->
          <div class="gs-card" :class="hasOrg ? 'gs-card-completed' : 'gs-card-pending'">
            <div class="gs-card-badge">2</div>
            
            <div class="gs-card-image">
              <img src="@/assets/images/temp-img/org-complete.svg" alt="Create Org" />
            </div>
            
            <h3 class="gs-card-title">Create an organization</h3>
            <p class="gs-card-desc">Set up your startup, team, or program to manage roles, coaching workflows, and growth journeys.</p>
            
            <!-- Organization Icon List (Moved to next line above the button to keep ticks aligned) -->
            <div v-if="hasOrg" class="flex flex-wrap gap-2 mb-5">
              <vx-tooltip 
                v-for="org in validUserOrgs" 
                :key="org.organizationId"
                :text="getOrgName(org)" 
                position="bottom"
              >
                <!-- Strictly small size using Tailwind w-8 h-8 (32px) -->
                <div 
                  class="w-8 h-8 flex-shrink-0 rounded-full border border-gray-200 shadow-sm overflow-hidden bg-white flex items-center justify-center transition-all"
                  :class="isAdmin(org) ? 'cursor-pointer hover:border-yellow-400 hover:scale-110' : 'opacity-90'"
                  @click="isAdmin(org) ? goToOrg(org.organizationData.slug) : null"
                >
                  <img :src="getOrgLogo(org)" class="w-full h-full object-cover" />
                </div>
              </vx-tooltip>
              
              <!-- Create Another Org (+) Button -->
              <vx-tooltip text="Create New Org" position="bottom">
                 <div 
                   class="w-8 h-8 flex-shrink-0 rounded-full border border-dashed border-gray-400 cursor-pointer flex items-center justify-center hover:bg-gray-100 transition-all text-gray-500 hover:text-gray-900"
                   @click="goToCreateOrg"
                 >
                   <feather-icon icon="PlusIcon" svgClasses="w-4 h-4" />
                 </div>
              </vx-tooltip>
            </div>

            <!-- Action Tick anchored to bottom -->
            <div class="gs-card-action mt-auto">
              <!-- If NO organizations exist -->
              <button v-if="!hasOrg" @click="goToCreateOrg" class="gs-btn-primary flex-shrink-0">
                Create organization <feather-icon icon="ArrowRightIcon" svgClasses="w-4 h-4" />
              </button>
              
              <!-- If User belongs to AT LEAST ONE organization -->
              <div v-else class="gs-btn-completed inline-flex flex-shrink-0">
                <feather-icon icon="CheckCircleIcon" svgClasses="w-4 h-4" /> 
                {{ hasCreatedOrg ? 'Organization Created' : 'Joined Organization' }}
              </div>
            </div>

          </div>

        </div>

        <!-- Footer Help Box -->
        <div class="gs-footer-box">
          <div class="gs-footer-icon">
            <feather-icon icon="InfoIcon" svgClasses="w-5 h-5" />
          </div>
          <div class="gs-footer-content">
            <h4>Not sure where to start?</h4>
            <p>Start with your profile if you're joining as an individual. Create an organization if you're setting up a team or startup. Founders will usually benefit from doing both.</p>
          </div>
        </div>

      </div>

    </div>
  </div>
</template>

<script>
import constants from "@/constants";

export default {
  name: "GettingStarted",
  props: {
    isModal: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      constants
    };
  },
  computed: {
    userData() {
      return this.$store.state.users.data;
    },
    userOrganizations() {
      return this.$store.state.users.userOrganizations;
    },
    validUserOrgs() {
      if (!this.userOrganizations || !Array.isArray(this.userOrganizations)) return [];
      
      const uniqueOrgs = [];
      const seenIds = new Set();
      
      for (const org of this.userOrganizations) {
        if (org && org.organizationData && org.organizationData.slug) {
          // FIX: Wrapped the status value in String() to prevent TypeError if status is a number
          const rawStatus = org.organizationData.status || org.status || 'active';
          const status = String(rawStatus).toLowerCase();
          
          const isDeactivated = status === 'inactive' || status === 'deactivated' || status === 'deleted';
          
          if (!isDeactivated && !seenIds.has(org.organizationId)) {
            seenIds.add(org.organizationId);
            uniqueOrgs.push(org);
          }
        }
      }
      return uniqueOrgs;
    },
    firstOrgName() {
      if (this.validUserOrgs.length > 0) {
        return this.validUserOrgs[0].organizationData.name;
      }
      return null;
    },
    hasProfile() {
      if (!this.userData) return false;
      
      const edu = this.userData.educationalQualifications || [];
      const career = this.userData.careerHistory || [];
      const skills = this.userData.skills || [];
      
      const validEdu = edu.some(e => e && (e.degree || e.school || e.fieldOfStudy));
      const validCareer = career.some(c => c && (c.title || c.company));
      const validSkills = skills.some(s => s && typeof s === 'string' && s.trim() !== '');
      
      return validEdu || validCareer || validSkills;
    },
    hasOrg() {
      return this.validUserOrgs.length > 0;
    },
    hasCreatedOrg() {
      return this.validUserOrgs.some(org => this.isAdmin(org));
    },
    completedCount() {
      let count = 0;
      if (this.hasProfile) count++;
      if (this.hasOrg) count++;
      return count;
    }
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    goToCreateOrg() {
      if (this.isModal) this.closeModal();
      this.$router.push('/organization/create');
    },
    isAdmin(org) {
      if (!org) return false;
      // FIX: Also applied String() safety wrapper here
      const role = String(org.role || org.userRole || '').toLowerCase();
      return role === 'admin' || role === 'owner' || role === 'creator';
    },
    getOrgName(org) {
      return org.organizationData ? org.organizationData.name : 'Organization';
    },
    getOrgLogo(org) {
      if (org.organizationData && org.organizationData.logo) {
        return this.constants.s3Bucket + '/organization/logo/' + org.organizationData.logo;
      }
      return require('@/assets/images/icons/organization/business_black_36dp.svg');
    },
    goToOrg(slug) {
      if (this.isModal) this.closeModal();
      window.location.href = `/organization/${slug}/home`;
    },
    shareTalentCard() {
      // Safely grab the user ID (prioritizing ID since our backend is setup for it now)
      const identifier = this.userData && (this.userData._id || this.userData.slug);
      
      if (!identifier) {
        this.$vs.notify({ title: 'Error', text: 'Could not generate link. User data missing.', color: 'danger' });
        return;
      }

      // Generate the public route URL we created earlier
      const shareUrl = `${window.location.origin}/p/${identifier}`;

      // Copy to clipboard
      navigator.clipboard.writeText(shareUrl).then(() => {
        this.$vs.notify({
          title: 'Link Copied!',
          text: 'Public Talent Card link copied to clipboard.',
          color: 'success',
          iconPack: 'feather',
          icon: 'icon-check'
        });
      }).catch(err => {
        console.error("Clipboard copy failed", err);
        this.$vs.notify({ title: 'Error', text: 'Failed to copy link.', color: 'danger' });
      });
    },
    async editProfile() {
      if (this.userData && this.userData._id) {
        try {
          const secret = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ';
          const header = { alg: 'HS256', typ: 'JWT' };
          const payload = {
            _id: this.userData._id,
            email: this.userData.email || '',
            iat: Math.floor(Date.now() / 1000),
            exp: Math.floor(Date.now() / 1000) + (60 * 60 * 24),
          };

          const base64Url = (str) => {
            return btoa(unescape(encodeURIComponent(str)))
              .replace(/\+/g, '-')
              .replace(/\//g, '_')
              .replace(/=+$/, '');
          };

          const encodedHeader = base64Url(JSON.stringify(header));
          const encodedPayload = base64Url(JSON.stringify(payload));
          const signatureInput = `${encodedHeader}.${encodedPayload}`;

          const enc = new TextEncoder();
          const key = await window.crypto.subtle.importKey(
            'raw',
            enc.encode(secret),
            { name: 'HMAC', hash: 'SHA-256' },
            false,
            ['sign']
          );

          const signatureBuffer = await window.crypto.subtle.sign(
            'HMAC',
            key,
            enc.encode(signatureInput)
          );

          const signatureArray = Array.from(new Uint8Array(signatureBuffer));
          const signatureBase64 = btoa(String.fromCharCode.apply(null, signatureArray))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');

          const token = `${encodedHeader}.${encodedPayload}.${signatureBase64}`;
          const org = 'default';
          const url = `https://editor.getboarded.com/oauth-callback?organization=${org}&token=${token}&userId=${this.userData._id}`;

          if (this.isModal) this.closeModal();
          window.open(url, '_blank');
        } catch (e) {
          console.error("Error generating token/redirecting:", e);
        }
      }
    }
  }
};
</script>

<style scoped>
/* ==========================================================================
   MODAL CSS
   ========================================================================== */
.gs-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(4px);
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.gs-modal-container {
  position: relative;
  background-color: #ffffff;
  width: 60%;
  max-width: 1000px;
  min-width: 320px;
  max-height: 85vh;
  overflow-y: auto;
  border-radius: 1rem;
  padding: 2.5rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  text-align: left;
}

/* ==========================================================================
   PAGE CSS
   ========================================================================== */
.gs-page-wrapper {
  width: 100%;
  height: 100%;
  display: block; 
}

.gs-page-container {
  background-color: #ffffff;
  width: 100%;
  min-height: calc(100vh - 120px);
  border-radius: 0.5rem;
  padding: 2.5rem;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  text-align: left;
}

.gs-page-inner-content {
  width: 100%;
  max-width: 100%; 
  margin: 0;
}

/* ==========================================================================
   SHARED UI ELEMENTS
   ========================================================================== */
.gs-close-btn {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  padding: 0.25rem;
  z-index: 10;
  transition: color 0.2s;
}
.gs-close-btn:hover {
  color: #374151;
}

.gs-header {
  margin-bottom: 2rem;
  padding-right: 3rem;
}
.gs-invite-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #fefce8;
  color: #854d0e;
  border: 1px solid #fef08a;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 1rem;
}
.gs-title {
  font-size: 1.875rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
}
.gs-subtitle {
  color: #4b5563;
  font-size: 0.9375rem;
  margin-bottom: 1.25rem;
}

/* Progress Bar */
.gs-progress-wrapper {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: #6b7280;
}
.gs-progress-label {
  background-color: #f3f4f6;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  color: #374151;
}
.gs-progress-track {
  width: 12rem;
  height: 0.5rem;
  background-color: #f3f4f6;
  border-radius: 9999px;
  overflow: hidden;
}
.gs-progress-fill {
  height: 100%;
  background-color: #facc15;
  transition: width 0.5s ease;
}

/* Cards Grid */
.gs-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

/* Card Styling with dynamic border controls */
.gs-card {
  position: relative;
  border-radius: 0.75rem;
  padding: 1.5rem;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.gs-card-pending {
  border: 1px solid #000000; /* Distinct Black outline when incomplete */
}

.gs-card-completed {
  border: 1px solid #facc15; /* Active Yellow outline when complete */
}

.gs-card:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

/* Card Step Badge */
.gs-card-badge {
  position: absolute;
  top: -1rem;
  left: -1rem;
  width: 2.25rem;
  height: 2.25rem;
  background-color: #facc15;
  border: 4px solid #ffffff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
}

/* Card Internal Styling */
.gs-card-image {
  height: 10rem;
  display: flex;
  justify-content: center;
  margin-bottom: 1.25rem;
}
.gs-card-image img {
  height: 100%;
  object-fit: contain;
}
.gs-card-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #ca8a04;
  margin-bottom: 0.5rem;
}
.gs-card-desc {
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

/* Buttons */
.gs-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #facc15;
  color: #000000;
  padding: 0.6rem 1.25rem;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
}
.gs-btn-primary:hover {
  background-color: #eab308;
}
.gs-btn-completed {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #dcfce7;
  color: #15803d;
  border: 1px solid #bbf7d0;
  padding: 0.6rem 1.25rem;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 0.875rem;
}

/* Footer Box */
.gs-footer-box {
  background-color: #fefce8;
  border: 1px solid #fef08a;
  border-radius: 0.75rem;
  padding: 1.25rem;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}
.gs-footer-icon {
  background-color: #ffffff;
  color: #eab308;
  padding: 0.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}
.gs-footer-content h4 {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.25rem;
}
.gs-footer-content p {
  font-size: 0.875rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
}

@media (max-width: 1024px) {
  .gs-modal-container {
    width: 85%;
  }
}
@media (max-width: 768px) {
  .gs-modal-container, .gs-page-container {
    width: 100%;
    padding: 1.5rem;
  }
  .gs-overlay {
    padding: 1rem;
  }
  .gs-cards-grid {
    grid-template-columns: 1fr;
  }
}
</style>